import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/services/supabase';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import { useAuthStore } from '@/stores/authStore';
import ChangePasswordForm from '@/components/settings/ChangePasswordForm';
import toast from 'react-hot-toast';

interface GlobalSetting {
  id: string;
  key: string;
  value: unknown;
  description: string | null;
  updated_at: string;
}

type LaunchMode = 'pre_launch' | 'partial_launch' | 'full_launch';

const LAUNCH_STAGES: Array<{
  value: LaunchMode;
  label: string;
  badge: string;
  description: string;
  confirm: string;
  borderClass: string;
  dotClass: string;
  badgeClass: string;
}> = [
  {
    value: 'pre_launch',
    label: 'Pre-Launch (waitlist)',
    badge: 'PRE-LAUNCH MODE',
    description: 'All users are being sent to the waitlist, regardless of location.',
    confirm: 'Switch to pre-launch? All users will go to the waitlist, including NY/NJ.',
    borderClass: 'border-orange-400',
    dotClass: 'bg-orange-500',
    badgeClass: 'bg-orange-100 text-orange-800',
  },
  {
    value: 'partial_launch',
    label: 'Partial Launch (NY/NJ)',
    badge: 'LAUNCHED - NY/NJ',
    description: 'NY/NJ users receive access codes. Other locations go to waitlist.',
    confirm: 'Switch to partial launch? Only NY/NJ users will receive access codes; everyone else goes to the waitlist.',
    borderClass: 'border-green-400',
    dotClass: 'bg-green-500',
    badgeClass: 'bg-green-100 text-green-800',
  },
  {
    value: 'full_launch',
    label: 'Full Launch (all US)',
    badge: 'LAUNCHED - ALL US',
    description: 'Every valid US ZIP code receives an access code. Nobody is sent to the waitlist.',
    confirm: 'Switch to FULL LAUNCH? Every valid US ZIP code will receive an access code and nobody will be waitlisted.',
    borderClass: 'border-blue-400',
    dotClass: 'bg-blue-500',
    badgeClass: 'bg-blue-100 text-blue-800',
  },
];

export default function Settings() {
  const { role, hasPermission, user } = useAuthStore();
  const queryClient = useQueryClient();
  const canWrite = hasPermission('settings', 'write');

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['globalSettings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('global_settings')
        .select('*')
        .order('key');
      if (error) throw error;
      return data as GlobalSetting[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const { error } = await supabase
        .from('global_settings')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('key', key);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Setting updated');
      queryClient.invalidateQueries({ queryKey: ['globalSettings'] });
      setEditingKey(null);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update setting');
    },
  });

  const handleSave = (key: string) => {
    try {
      const parsedValue = JSON.parse(editValue);
      updateMutation.mutate({ key, value: parsedValue });
    } catch {
      // If not valid JSON, save as string
      updateMutation.mutate({ key, value: editValue });
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Global platform settings and configuration
        </p>
      </div>

      {/* Launch Control - Launch Stage Selector */}
      {(() => {
        const launchSetting = settings?.find(s => s.key === 'launch_mode');
        const preLaunchSetting = settings?.find(s => s.key === 'pre_launch_mode');

        const normalize = (value: unknown) =>
          typeof value === 'string' ? value.replace(/"/g, '').trim().toLowerCase() : String(value ?? '');

        const rawMode = normalize(launchSetting?.value);
        const legacyPreLaunch = normalize(preLaunchSetting?.value) === 'true';
        const launchMode: LaunchMode =
          rawMode === 'pre_launch' || rawMode === 'partial_launch' || rawMode === 'full_launch'
            ? rawMode
            : legacyPreLaunch
              ? 'pre_launch'
              : 'partial_launch';

        const stage = LAUNCH_STAGES.find(s => s.value === launchMode)!;
        const updatedAt = launchSetting?.updated_at || preLaunchSetting?.updated_at;

        return (
          <Card className={`border-2 ${stage.borderClass}`}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${stage.dotClass}`} />
                <h2 className="text-lg font-semibold">Launch Control</h2>
              </div>
            </CardHeader>
            <CardBody>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${stage.badgeClass}`}>
                  {stage.badge}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-2">{stage.description}</p>
              {updatedAt && (
                <p className="text-xs text-gray-400 mt-1">
                  Last updated: {new Date(updatedAt).toLocaleString()}
                </p>
              )}

              {canWrite && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Launch stage</p>
                  <div className="flex flex-wrap gap-2">
                    {LAUNCH_STAGES.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          if (option.value === launchMode) return;
                          if (window.confirm(option.confirm)) {
                            updateMutation.mutate({ key: 'launch_mode', value: option.value });
                          }
                        }}
                        disabled={updateMutation.isPending}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          option.value === launchMode
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        } ${updateMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Users outside an open area are added to the waitlist. Users inside it receive an
                    access code by email.
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        );
      })()}

      {/* OTP & Trusted Devices Settings */}
      {(() => {
        const trustedDeviceEnabledSetting = settings?.find(s => s.key === 'trusted_device_enabled');
        const reverificationDaysSetting = settings?.find(s => s.key === 'phone_reverification_days');

        const isTrustedDeviceEnabled = trustedDeviceEnabledSetting?.value === true ||
                                       trustedDeviceEnabledSetting?.value === 'true';
        const reverificationDays = parseInt(String(reverificationDaysSetting?.value || '30'));

        const durationOptions = [
          { value: 7, label: '7 days' },
          { value: 14, label: '14 days' },
          { value: 30, label: '30 days' },
          { value: 60, label: '60 days' },
          { value: 90, label: '90 days' },
        ];

        return (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <h2 className="text-lg font-semibold">OTP & Trusted Devices</h2>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full ${isTrustedDeviceEnabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <h3 className="font-medium text-gray-900">Trusted Device Login</h3>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 ml-6">
                      {isTrustedDeviceEnabled
                        ? 'Users can skip OTP on trusted devices within the re-verification period'
                        : 'Users must verify with OTP every login'}
                    </p>
                  </div>
                  {canWrite && (
                    <button
                      onClick={() => {
                        const newValue = !isTrustedDeviceEnabled;
                        const confirmMsg = newValue
                          ? 'Enable trusted devices? Users will be able to skip OTP verification.'
                          : 'Disable trusted devices? All users will need OTP on every login.';
                        if (window.confirm(confirmMsg)) {
                          updateMutation.mutate({ key: 'trusted_device_enabled', value: newValue });
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        isTrustedDeviceEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                      disabled={updateMutation.isPending}
                      aria-label={isTrustedDeviceEnabled ? 'Disable trusted device login' : 'Enable trusted device login'}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          isTrustedDeviceEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  )}
                </div>

                {/* Re-verification Period */}
                {isTrustedDeviceEnabled && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Re-verification Period</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      How long before users must verify with OTP again on a trusted device
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {durationOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            if (canWrite) {
                              updateMutation.mutate({ key: 'phone_reverification_days', value: String(option.value) });
                            }
                          }}
                          disabled={!canWrite || updateMutation.isPending}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            reverificationDays === option.value
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          } ${!canWrite ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Current: Users must re-verify every <strong>{reverificationDays} days</strong>
                    </p>
                  </div>
                )}

                {/* Info Box */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex gap-3">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">How it works:</p>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>After OTP verification, the device is registered as trusted</li>
                        <li>On subsequent logins from that device, OTP is skipped</li>
                        <li>After the re-verification period, OTP is required again</li>
                        <li>Users can view and revoke devices in their security settings</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        );
      })()}

      {/* Admin Info */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Admin Account</h2>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-gray-900">{user?.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Role</dt>
              <dd className="mt-1 text-gray-900 capitalize">{role?.replace('_', ' ')}</dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Change Password</h2>
        </CardHeader>
        <CardBody>
          <ChangePasswordForm />
        </CardBody>
      </Card>

      {/* Global Settings */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Global Settings</h2>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : settings && settings.length > 0 ? (
            <div className="space-y-4">
              {settings.map((setting) => (
                <div
                  key={setting.id}
                  className="p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{setting.key}</h3>
                      {setting.description && (
                        <p className="text-sm text-gray-500 mt-1">{setting.description}</p>
                      )}

                      {editingKey === setting.key ? (
                        <div className="mt-2">
                          <textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand font-mono text-sm"
                            rows={3}
                            aria-label={`Edit setting ${setting.key}`}
                          />
                          <div className="mt-2 flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSave(setting.key)}
                              loading={updateMutation.isPending}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setEditingKey(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <pre className="mt-2 p-2 bg-gray-50 rounded text-sm overflow-x-auto">
                          {JSON.stringify(setting.value, null, 2)}
                        </pre>
                      )}
                    </div>

                    {canWrite && editingKey !== setting.key && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingKey(setting.key);
                          setEditValue(JSON.stringify(setting.value, null, 2));
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No settings configured</p>
          )}
        </CardBody>
      </Card>

      {/* Permissions */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Your Permissions</h2>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {[
              { label: 'Read Users', check: () => hasPermission('users', 'read') },
              { label: 'Suspend Users', check: () => hasPermission('users', 'suspend') },
              { label: 'Ban Users', check: () => hasPermission('users', 'ban') },
              { label: 'Read Businesses', check: () => hasPermission('businesses', 'read') },
              { label: 'Moderate Businesses', check: () => hasPermission('businesses', 'moderate') },
              { label: 'Manage Subscriptions', check: () => hasPermission('businesses', 'subscription_control') },
              { label: 'Read Analytics', check: () => hasPermission('analytics', 'read') },
              { label: 'Export Data', check: () => hasPermission('analytics', 'export') },
              { label: 'Read Payments', check: () => hasPermission('payments', 'read') },
              { label: 'Refund Payments', check: () => hasPermission('payments', 'refund') },
              { label: 'Resolve Moderation', check: () => hasPermission('moderation', 'resolve') },
              { label: 'Issue Violations', check: () => hasPermission('moderation', 'violation') },
              { label: 'Read Audit Logs', check: () => hasPermission('audit_logs', 'read') },
              { label: 'Write Settings', check: () => hasPermission('settings', 'write') },
            ].map((perm) => (
              <div key={perm.label} className="flex items-center gap-2">
                <span
                  className={`w-4 h-4 rounded-full ${
                    perm.check() ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                />
                <span className="text-sm text-gray-700">{perm.label}</span>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
